import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../../lib/supabase';
import { refreshGlobalSettings } from '../../../../hooks/useSystemSettings';
import { useAuth } from '../../../../contexts/AuthContext';

interface AIConfig {
  ai_enabled_global: string;
  ai_environment: string;
  ai_provider: string;
  ai_model: string;
  ai_api_key: string;
  ai_timeout_seconds: string;
  ai_temperature: string;
  ai_max_tokens: string;
  ai_system_prompt: string;
  ai_welcome_message: string;
  ai_fallback_message: string;
  ai_out_of_scope_message: string;
  ai_enable_usage_logs: string;
  ai_enable_history: string;
  ai_enable_context_memory: string;
  ai_enable_proactive_insights: string;
  ai_can_read_summary: string;
  ai_can_read_categories: string;
  ai_can_read_payees: string;
  ai_can_read_accounts: string;
  ai_can_read_goals: string;
  ai_can_read_reports: string;
  ai_button_icon_url: string;
  ai_assistant_name: string;
}

const DEFAULT: AIConfig = {
  ai_enabled_global: 'false', ai_environment: 'all', ai_provider: 'openai',
  ai_model: 'gpt-4o-mini', ai_api_key: '', ai_timeout_seconds: '30',
  ai_temperature: '0.3', ai_max_tokens: '500',
  ai_system_prompt: '', ai_welcome_message: 'Olá! Sou o Panda AI, seu assistente financeiro. Pergunte sobre seus gastos, metas ou saldo!',
  ai_fallback_message: 'Desculpe, não consegui processar sua pergunta. Tente novamente.',
  ai_out_of_scope_message: 'Sou especializado em finanças pessoais. Como posso ajudar com suas finanças?',
  ai_enable_usage_logs: 'true', ai_enable_history: 'true',
  ai_enable_context_memory: 'false', ai_enable_proactive_insights: 'false',
  ai_can_read_summary: 'true', ai_can_read_categories: 'true',
  ai_can_read_payees: 'true', ai_can_read_accounts: 'true',
  ai_can_read_goals: 'true', ai_can_read_reports: 'false',
  ai_button_icon_url: '',
  ai_assistant_name: 'Panda AI',
};

const ALL_KEYS = Object.keys(DEFAULT);

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI', models: ['gpt-4o-mini', 'gpt-4o', 'gpt-3.5-turbo'] },
  { value: 'groq', label: 'Groq (rápido/grátis)', models: ['llama-3.1-8b-instant', 'llama-3.3-70b-versatile', 'mixtral-8x7b-32768'] },
];

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button type="button" onClick={() => onChange(!value)}
    className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-emerald-500' : 'bg-white/10'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
  </button>
);

const ToggleRow = ({ icon, label, hint, k, editing, setEditing }: {
  icon: string; label: string; hint?: string; k: keyof AIConfig;
  editing: AIConfig; setEditing: (v: AIConfig) => void;
}) => (
  <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
    <div className="flex items-center gap-2.5">
      <i className={`${icon} text-white/30 text-[14px]`} />
      <div>
        <p className="text-[13px] text-white/70">{label}</p>
        {hint && <p className="text-[11px] text-white/30">{hint}</p>}
      </div>
    </div>
    <Toggle value={editing[k] === 'true'} onChange={v => setEditing({ ...editing, [k]: v ? 'true' : 'false' })} />
  </div>
);

export const PandaAIIntegration = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<AIConfig>(DEFAULT);
  const [editing, setEditing] = useState<AIConfig>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState('');
  const iconInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('system_settings').select('key, value').in('key', ALL_KEYS);
    if (data) {
      const map: Partial<AIConfig> = {};
      data.forEach((r: { key: string; value: string }) => { (map as Record<string, string>)[r.key] = r.value ?? ''; });
      const merged = { ...DEFAULT, ...map };
      setConfig(merged); setEditing(merged);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    const rows = Object.entries(editing).map(([key, value]) => ({ key, value: String(value) }));
    await supabase.from('system_settings').upsert(rows, { onConflict: 'key' });
    await refreshGlobalSettings();
    setConfig({ ...editing }); setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const ERROR_MESSAGES: Record<string, string> = {
    missing_token: 'Sua sessão expirou. Faça login novamente.',
    invalid_token: 'Token inválido. Faça login novamente.',
    session_expired: 'Sua sessão expirou. Faça login novamente.',
    auth_error: 'Erro de autenticação. Faça login novamente.',
    no_user: 'Usuário não encontrado. Faça login novamente.',
    no_permission: 'Você não tem permissão para testar esta integração.',
    no_api_key: 'Nenhuma chave de API configurada. Insira a API Key antes de testar.',
    invalid_api_key: 'Chave de API inválida. Verifique se a chave está correta e ativa.',
    invalid_model: 'Modelo inválido ou indisponível. Selecione um modelo compatível com o provider.',
    rate_limit: 'Limite de requisições da API atingido. Aguarde e tente novamente.',
    quota_exceeded: 'Cota ou crédito da API esgotado. Verifique o saldo na conta do provider.',
    timeout: 'Timeout: o servidor da IA não respondeu a tempo. Aumente o timeout ou verifique o provider.',
    network_error: 'Erro de conexão com a API da IA. Verifique a URL do provider.',
    api_error: 'Erro retornado pela API da IA.',
    internal_error: 'Erro interno. Tente novamente em alguns instantes.',
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      // Force session refresh to guarantee a valid token
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      const session = refreshData?.session;

      if (refreshError || !session) {
        // Fallback: try getSession
        const { data: { session: fallbackSession } } = await supabase.auth.getSession();
        if (!fallbackSession) {
          setTestResult({ ok: false, message: 'Sua sessão expirou. Faça login novamente.' });
          setTesting(false);
          return;
        }
        // Use fallback session
        await executeTest(fallbackSession.access_token);
        return;
      }

      await executeTest(session.access_token);
    } catch (e) {
      setTestResult({ ok: false, message: `Erro inesperado: ${(e as Error).message}` });
    }
    setTesting(false);
  };

  const executeTest = async (accessToken: string) => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/process-user-question`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ question: 'Teste de integração', is_test: true }),
        }
      );

      let data: { success?: boolean; message?: string; model?: string; provider?: string; error?: string; tokens_used?: number };
      try {
        data = await res.json();
      } catch {
        setTestResult({ ok: false, message: `Erro HTTP ${res.status}: resposta inválida do servidor.` });
        setTesting(false);
        return;
      }

      if (data.success) {
        const preview = data.message ? `"${data.message.substring(0, 100)}${data.message.length > 100 ? '...' : ''}"` : '';
        setTestResult({
          ok: true,
          message: `Integração testada com sucesso! Provider: ${data.provider} | Modelo: ${data.model} | Tokens: ${data.tokens_used ?? 0} — ${preview}`,
        });
      } else {
        const errorCode = data.error || '';
        const friendlyMessage = ERROR_MESSAGES[errorCode] || data.message || 'Erro desconhecido.';
        setTestResult({ ok: false, message: friendlyMessage });
      }
    } catch (e) {
      setTestResult({ ok: false, message: `Erro de rede: ${(e as Error).message}` });
    } finally {
      setTesting(false);
    }
  };

  const hasChanges = JSON.stringify(editing) !== JSON.stringify(config);
  const isEnabled = editing.ai_enabled_global === 'true';
  const selectedProvider = PROVIDERS.find(p => p.value === editing.ai_provider) || PROVIDERS[0];

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { setIconError('Sessão não encontrada. Recarregue a página.'); return; }
    if (!file.type.startsWith('image/')) { setIconError('Apenas imagens são aceitas.'); return; }
    if (file.size > 2 * 1024 * 1024) { setIconError('Imagem muito grande. Máximo 2MB.'); return; }
    setUploadingIcon(true); setIconError('');
    try {
      const ext = file.name.split('.').pop() ?? 'png';
      // Path must follow the RLS policy pattern: {userId}/filename
      const path = `${user.id}/ai-button-icon-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const url = urlData.publicUrl;
      setEditing(p => ({ ...p, ai_button_icon_url: url }));
      // Save immediately to system_settings
      await supabase.from('system_settings').upsert([{ key: 'ai_button_icon_url', value: url }], { onConflict: 'key' });
      await refreshGlobalSettings();
      setConfig(p => ({ ...p, ai_button_icon_url: url }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro desconhecido';
      setIconError(`Erro ao enviar imagem: ${msg}`);
    } finally {
      setUploadingIcon(false);
      if (iconInputRef.current) iconInputRef.current.value = '';
    }
  };

  const handleRemoveIcon = async () => {
    setEditing(p => ({ ...p, ai_button_icon_url: '' }));
    await supabase.from('system_settings').upsert([{ key: 'ai_button_icon_url', value: '' }], { onConflict: 'key' });
    await refreshGlobalSettings();
    setConfig(p => ({ ...p, ai_button_icon_url: '' }));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line text-3xl text-emerald-400 animate-spin" /></div>;

  return (
    <div className="space-y-5">
      {/* Status cards */}      <div className="grid grid-cols-3 gap-4">
        <div className={`border rounded-2xl p-4 flex items-center gap-3 ${isEnabled ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-white/10 bg-white/5'}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${isEnabled ? 'bg-emerald-500/20' : 'bg-white/10'}`}>
            <i className={`ri-robot-2-line text-[18px] ${isEnabled ? 'text-emerald-400' : 'text-white/30'}`} />
          </div>
          <div>
            <p className="text-[11px] text-white/30">Panda AI</p>
            <p className={`text-[15px] font-700 ${isEnabled ? 'text-emerald-300' : 'text-white/50'}`}>{isEnabled ? 'Ativo' : 'Inativo'}</p>
          </div>
        </div>
        <div className="border border-white/10 bg-white/5 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 shrink-0">
            <i className="ri-cpu-line text-[18px] text-white/30" />
          </div>
          <div>
            <p className="text-[11px] text-white/30">Provider / Modelo</p>
            <p className="text-[13px] font-600 text-white/70">{editing.ai_provider} / {editing.ai_model}</p>
          </div>
        </div>
        <div className="border border-white/10 bg-white/5 rounded-2xl p-4 flex items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl shrink-0 ${editing.ai_api_key ? 'bg-emerald-600/20' : 'bg-red-500/20'}`}>
            <i className={`ri-key-2-line text-[18px] ${editing.ai_api_key ? 'text-emerald-400' : 'text-red-400'}`} />
          </div>
          <div>
            <p className="text-[11px] text-white/30">API Key</p>
            <p className={`text-[13px] font-600 ${editing.ai_api_key ? 'text-emerald-400' : 'text-red-400'}`}>{editing.ai_api_key ? 'Configurada' : 'Não configurada'}</p>
          </div>
        </div>
      </div>

      {/* Main config */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-500/20 rounded-xl">
              <i className="ri-robot-2-line text-emerald-400 text-[18px]" />
            </div>
            <div>
              <h2 className="text-[15px] font-700 text-white">Panda AI — Configuração Global</h2>
              <p className="text-[11px] text-white/30">Assistente financeiro com IA para todos os usuários</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saved && <span className="flex items-center gap-1.5 text-[12px] font-600 text-emerald-400"><i className="ri-checkbox-circle-line" />Salvo</span>}
            <button onClick={handleTest} disabled={testing || !editing.ai_api_key}
              className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-white/40 text-white/70 hover:text-white text-[12px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40">
              {testing ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-test-tube-line" />}
              {testing ? 'Testando...' : 'Testar integração'}
            </button>
          </div>
        </div>

        {testResult && (
          <div className={`mx-6 mt-4 flex items-start gap-3 p-4 rounded-xl border ${testResult.ok ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <i className={`${testResult.ok ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-close-circle-line text-red-400'} text-[18px] shrink-0 mt-0.5`} />
            <p className="text-[13px] text-white/70">{testResult.message}</p>
            <button onClick={() => setTestResult(null)} className="ml-auto text-white/30 hover:text-white/60 cursor-pointer"><i className="ri-close-line" /></button>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Enable */}
          <div className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="flex items-center gap-3">
              <i className="ri-toggle-line text-[20px] text-emerald-400" />
              <div>
                <p className="text-[14px] font-700 text-white">Ativar Panda AI globalmente</p>
                <p className="text-[11px] text-white/40 mt-0.5">Habilita o assistente para todos os usuários que tiverem o recurso no plano</p>
              </div>
            </div>
            <Toggle value={isEnabled} onChange={v => setEditing(p => ({ ...p, ai_enabled_global: v ? 'true' : 'false' }))} />
          </div>

          {/* Assistant Name */}
          <div>
            <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">
              Nome do Assistente
            </label>
            <input
              type="text"
              value={editing.ai_assistant_name}
              onChange={e => setEditing(p => ({ ...p, ai_assistant_name: e.target.value }))}
              placeholder="Panda AI"
              maxLength={40}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-emerald-500 placeholder:text-white/20"
            />
            <p className="text-[11px] text-white/25 mt-1">Aparece no botão flutuante, cabeçalho do chat e mensagens. Ex: "Finny", "BankBot", "Panda AI"</p>
          </div>

          {/* Provider & Model */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Provider</label>
              <select value={editing.ai_provider}
                onChange={e => setEditing(p => ({ ...p, ai_provider: e.target.value, ai_model: PROVIDERS.find(pr => pr.value === e.target.value)?.models[0] || p.ai_model }))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-emerald-500 cursor-pointer">
                {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">Modelo</label>
              <select value={editing.ai_model}
                onChange={e => setEditing(p => ({ ...p, ai_model: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-emerald-500 cursor-pointer">
                {selectedProvider.models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* API Key */}
          <div>
            <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">API Key (armazenada com segurança no servidor)</label>
            <div className="relative">
              <input type={showKey ? 'text' : 'password'} value={editing.ai_api_key}
                onChange={e => setEditing(p => ({ ...p, ai_api_key: e.target.value }))}
                placeholder="sk-... ou gsk_..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 pr-10 text-white text-[13px] font-mono outline-none focus:border-emerald-500 placeholder:text-white/20" />
              <button type="button" onClick={() => setShowKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 cursor-pointer">
                <i className={showKey ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
            <p className="text-[11px] text-white/25 mt-1">A chave nunca é exposta ao frontend — processada exclusivamente via Edge Function</p>
          </div>

          {/* Advanced params */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Temperature', key: 'ai_temperature' as keyof AIConfig, min: '0', max: '1', step: '0.1', hint: '0 = determinístico' },
              { label: 'Máx. tokens', key: 'ai_max_tokens' as keyof AIConfig, min: '100', max: '4000', hint: 'Por resposta' },
              { label: 'Timeout (s)', key: 'ai_timeout_seconds' as keyof AIConfig, min: '5', max: '120', hint: 'Tempo limite' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">{f.label}</label>
                <input type="number" min={f.min} max={f.max} step={f.step || '1'}
                  value={editing[f.key]}
                  onChange={e => setEditing(p => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] text-right outline-none focus:border-emerald-500" />
                <p className="text-[10px] text-white/25 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>

          {/* Behavior prompts */}
          <div className="space-y-3">
            {[
              { label: 'Mensagem de boas-vindas', key: 'ai_welcome_message' as keyof AIConfig, placeholder: 'Olá! Sou o Panda AI...' },
              { label: 'Mensagem de fallback (erro)', key: 'ai_fallback_message' as keyof AIConfig, placeholder: 'Não consegui processar...' },
              { label: 'Mensagem fora do escopo', key: 'ai_out_of_scope_message' as keyof AIConfig, placeholder: 'Sou especializado em finanças...' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">{f.label}</label>
                <input type="text" value={editing[f.key]}
                  onChange={e => setEditing(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-emerald-500 placeholder:text-white/20" />
              </div>
            ))}
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">System Prompt customizado (opcional)</label>
              <textarea rows={4} value={editing.ai_system_prompt}
                onChange={e => setEditing(p => ({ ...p, ai_system_prompt: e.target.value }))}
                placeholder="Deixe em branco para usar o prompt padrão do sistema..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-emerald-500 placeholder:text-white/20 resize-none" />
            </div>
          </div>

          {/* Controls & Data access */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/3 border border-white/8 rounded-2xl p-4">
              <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Controles</p>
              <ToggleRow icon="ri-history-line" label="Salvar histórico" hint="Registra perguntas e respostas" k="ai_enable_usage_logs" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-chat-history-line" label="Histórico no chat" hint="Exibe histórico na sessão" k="ai_enable_history" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-brain-line" label="Memória de contexto" hint="Mantém contexto entre perguntas" k="ai_enable_context_memory" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-notification-3-line" label="Insights proativos" hint="IA detecta padrões automaticamente" k="ai_enable_proactive_insights" editing={editing} setEditing={setEditing} />
            </div>
            <div className="bg-white/3 border border-white/8 rounded-2xl p-4">
              <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Dados que a IA pode acessar</p>
              <ToggleRow icon="ri-pie-chart-line" label="Resumo financeiro" k="ai_can_read_summary" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-folder-chart-line" label="Categorias" k="ai_can_read_categories" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-team-line" label="Favorecidos" k="ai_can_read_payees" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-bank-line" label="Contas" k="ai_can_read_accounts" editing={editing} setEditing={setEditing} />
              <ToggleRow icon="ri-flag-line" label="Metas" k="ai_can_read_goals" editing={editing} setEditing={setEditing} />
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <p className="text-[11px] text-white/25">Chave processada apenas no servidor — nunca exposta ao cliente</p>
            <button onClick={handleSave} disabled={saving || !hasChanges}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[13px] font-700 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </button>
          </div>
        </div>
      </div>

      {/* Icon Upload Section */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
          <div className="w-9 h-9 flex items-center justify-center bg-violet-500/20 rounded-xl">
            <i className="ri-image-line text-violet-400 text-[18px]" />
          </div>
          <div>
            <h2 className="text-[15px] font-700 text-white">Ícone do Botão Flutuante</h2>
            <p className="text-[11px] text-white/30">Personalize a aparência do botão do Panda AI na área do usuário</p>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6">
            {/* Preview */}
            <div className="flex flex-col items-center gap-2 shrink-0">
              <p className="text-[11px] text-white/40 uppercase tracking-wider font-600">Preview</p>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center overflow-hidden"
                style={{ boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}>
                {editing.ai_button_icon_url ? (
                  <img src={editing.ai_button_icon_url} alt="Ícone" className="w-10 h-10 object-contain rounded-xl" />
                ) : (
                  <i className="ri-robot-2-line text-2xl text-white" />
                )}
              </div>
              <p className="text-[10px] text-white/30">Tamanho real</p>
            </div>

            {/* Controls */}
            <div className="flex-1 space-y-4">
              {/* Upload button */}
              <div>
                <p className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-2">Upload de imagem</p>
                <div className="flex items-center gap-3">
                  <input ref={iconInputRef} type="file" accept="image/*" onChange={handleIconUpload} className="hidden" />
                  <button onClick={() => iconInputRef.current?.click()} disabled={uploadingIcon}
                    className="flex items-center gap-2 px-4 py-2.5 bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 text-violet-300 text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
                    {uploadingIcon
                      ? <><i className="ri-loader-4-line animate-spin" />Enviando...</>
                      : <><i className="ri-upload-2-line" />Escolher imagem</>}
                  </button>
                  {editing.ai_button_icon_url && (
                    <button onClick={handleRemoveIcon}
                      className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                      <i className="ri-delete-bin-line" />Remover
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-white/25 mt-1.5">PNG, JPG ou SVG. Máximo 2MB. Recomendado: 128×128px</p>
                {iconError && <p className="text-[12px] text-red-400 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line" />{iconError}</p>}
              </div>

              {/* Or URL */}
              <div>
                <p className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-2">Ou cole uma URL direta</p>
                <div className="flex items-center gap-2">
                  <input type="url" value={editing.ai_button_icon_url}
                    onChange={e => setEditing(p => ({ ...p, ai_button_icon_url: e.target.value }))}
                    placeholder="https://exemplo.com/icone-panda.png"
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20" />
                  {editing.ai_button_icon_url !== config.ai_button_icon_url && (
                    <button onClick={handleSave} disabled={saving}
                      className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors">
                      Salvar
                    </button>
                  )}
                </div>
              </div>

              {/* Tips */}
              <div className="flex items-start gap-2 p-3 bg-white/5 rounded-xl border border-white/8">
                <i className="ri-lightbulb-line text-amber-400 text-[14px] shrink-0 mt-0.5" />
                <p className="text-[12px] text-white/50 leading-relaxed">
                  <strong className="text-white/70">Dica:</strong> Use uma imagem com fundo transparente (PNG) para melhor resultado. O ícone aparece sobre o gradiente verde do botão.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
